/*
 *  ZAITEC CONFIDENTIAL
 *  __________________
 *
 * [2019] - [2020] zaiTEC Logixs Ltd
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of  zaiTEC Logixs Ltd and its suppliers,if any.
 *  The intellectual and technical concepts contained herein are proprietary to zaiTEC Logixs Ltd, and are protected by
 *  trade secret or copyright law.Dissemination of this information or reproduction of this material is strictly forbidden
 *  unless prior written permission is obtained from  zaiTEC Logixs Ltd.
 */

import { AuthGuardService } from '@/app/shared/guard/authGuard.service';
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { LoginRequest } from 'src/app/shared/model/LoginRequest';
import { UserResponse } from 'src/app/shared/model/UserResponse';
import { lastValueFrom, Observable, of, throwError } from 'rxjs';
import { catchError, retry, tap } from 'rxjs/operators';
import { AppUser } from '../../shared';
import { environment } from '../../../environments/environment';

import 'url-search-params-polyfill';
import { WebSocketMessage } from '../model/WebSocketMessage';
import { SharedDataService } from './shared.data.service';
import { WebSocketService } from '../websocket/WebSocketService';
import { Router } from '@angular/router';
import { GlobalErrorHandlingService } from '../ErrorHandler/GlobalErrorHanding.service';

const GET_OAUTH_TOKEN_URI: string = environment.API_URL + '/oauth/token';
const VALIDATE_APPUSERS_URI: string = environment.API_URL + '/appuser/validateappuser';
const CURRENT_APPUSER_URI: string = environment.API_URL + '/appuser/currentUser';
const httpOptions = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json',
    Authorization: 'my-auth-token',
  }),
};
@Injectable()
export class AuthService {
  // private headers = new Headers({'Content-Type': 'application/json'});
  public loggedIn;
  LoggedInUser: AppUser;

  constructor(
    private http: HttpClient,
    private errorService: GlobalErrorHandlingService,
    private webSocketService: WebSocketService,
    private sharedDataService: SharedDataService,
    public router: Router,
    private authGuardService: AuthGuardService,
  ) {
    this.sharedDataService.loggedInUserCast.subscribe((LoggedInUser) => (this.LoggedInUser = LoggedInUser));
  }

  login(loginRequest: LoginRequest): Observable<any> {
    const url = `${environment.API_BASE_URL}/auth/login`;
    return this.http.post<LoginRequest>(url, loginRequest, httpOptions).pipe(
      retry(3), // retry a failed request up to 3 times
      catchError((error) => this.errorService.handleError<any>('loginRequest')(error)), // Ensure returning an Observable
    );
  }

  private request(options: any): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    if (sessionStorage.getItem(environment.ACCESS_TOKEN)) {
      headers.append('Authorization', 'Bearer ' + sessionStorage.getItem(environment.ACCESS_TOKEN));
    }

    const defaults = { headers: headers };
    options = Object.assign({}, defaults, options);

    return this.http.request(options.method, options.url, options).pipe(
      catchError((error) => {
        return throwError(() => error);
      }),
    );
  }

  // App User Authorization
  getAppUsersValidation(userName: string, userPassword: string): Observable<any> {
    const url = VALIDATE_APPUSERS_URI + `/${userName}/${userPassword}`;
    return this.http.get<AppUser>(url).pipe(
      tap((_) => {
        console.log(`User successfully Loggedin`);
      }),
      catchError(this.errorService.handleError('getAppUsersValidation', '')), // then handle the error
    );
  }

  getCurrentAppUser(argAppUserId: number): Observable<any> {
    const url = CURRENT_APPUSER_URI + `/${argAppUserId}`;
    return this.http.get<UserResponse>(url).pipe(
      tap((_) => {
        console.log(`User successfully Loggedin`);
      }),
      catchError(this.errorService.handleError('getAppUsersValidation', '')), // then handle the error
    );
  }

  getCurrentUser(): Observable<any> {
    if (!sessionStorage.getItem(environment.ACCESS_TOKEN)) {
      return throwError(() => new Error('No access token set.'));
    }

    return this.request({
      url: `${environment.API_BASE_URL}/appuser/me`,
      method: 'GET',
    });
  }

  private sendLogoutMessage() {
    if (!this.LoggedInUser) return;
    const message = new WebSocketMessage(
      this.LoggedInUser.personId,
      99999, // admin to view the logged-in users
      `${this.LoggedInUser.firstName} ${this.LoggedInUser.lastName}`,
      this.LoggedInUser.userProfile,
      'LOGGEDOUT',
      null,
      null,
      null,
    );
    console.log('Send message to Admin as Logout');
    // Optionally send message to admin
    // this.webSocketService.sendWSMsgToAdmin(message);
  }

  logout() {
    this.sendLogoutMessage();
    this.webSocketService.disconnect();
    this.authGuardService.logout();
  }

  disconnectWSAndLogout() {
    this.sendLogoutMessage();
    this.webSocketService.disconnect();
    this.authGuardService.clearSessionAndResetState();
  }

  createSnakbarMessage() {
    this.webSocketService.refreshLogoutMessage('You refreshed the page and have been logged out. Please log in again.');
  }
}
